import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const PhotoGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [editingTags, setEditingTags] = useState(null);
  const [newPersonName, setNewPersonName] = useState('');
  const [newPersonRole, setNewPersonRole] = useState('primary_subject');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchImages();
    } else {
      setLoading(false);
      setError('Please log in to view your photo gallery');
    }
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/documents/images');
      setImages(response.data.images || []);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Failed to load photo gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (image) => {
    setViewingImage(image);
  };

  const closeViewer = () => {
    setViewingImage(null);
  };

  const toggleImageSelection = (imageId) => {
    setSelectedImages(prev =>
      prev.includes(imageId)
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  const selectAll = () => {
    if (selectedImages.length === filteredImages.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(filteredImages.map(img => img._id));
    }
  };

  const startEditingTags = (image, e) => {
    e.stopPropagation();
    setEditingTags(image._id);
    setNewPersonName('');
    setNewPersonRole('primary_subject');
  };

  const savePersonTag = async (imageId) => {
    if (!newPersonName.trim()) return;

    try {
      const image = images.find(img => img._id === imageId);
      const updatedPeople = [...(image.peopleIdentified || []), {
        name: newPersonName.trim(),
        role: newPersonRole
      }];

      await axios.put(`/api/documents/${imageId}`, {
        peopleIdentified: updatedPeople
      });

      // Update local state
      setImages(prev => prev.map(img =>
        img._id === imageId
          ? { ...img, peopleIdentified: updatedPeople }
          : img
      ));

      setNewPersonName('');
      setEditingTags(null);
    } catch (err) {
      console.error('Error saving person tag:', err);
      alert('Failed to save person tag');
    }
  };

  const removePerson = async (imageId, personIndex) => {
    try {
      const image = images.find(img => img._id === imageId);
      const updatedPeople = image.peopleIdentified.filter((_, index) => index !== personIndex);

      await axios.put(`/api/documents/${imageId}`, {
        peopleIdentified: updatedPeople
      });

      setImages(prev => prev.map(img =>
        img._id === imageId
          ? { ...img, peopleIdentified: updatedPeople }
          : img
      ));
    } catch (err) {
      console.error('Error removing person:', err);
    }
  };

  const filteredImages = images.filter(image => {
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesName = image.originalName.toLowerCase().includes(searchLower);
      const matchesPeople = image.peopleIdentified?.some(person =>
        person.name.toLowerCase().includes(searchLower)
      );
      const matchesTags = image.tags?.some(tag =>
        tag.toLowerCase().includes(searchLower)
      );
      if (!matchesName && !matchesPeople && !matchesTags) return false;
    }

    // Apply type filter
    if (filter === 'portraits' && !image.tags?.includes('portrait')) return false;
    if (filter === 'documents' && image.documentType === 'photo') return false;
    if (filter === 'untagged' && image.peopleIdentified?.length > 0) return false;

    return true;
  });

  const createAlbum = () => {
    if (selectedImages.length === 0) {
      alert('Please select images for your album');
      return;
    }
    // Navigate to album creation with selected images
    navigate('/album-creator', { state: { selectedImages } });
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem', textAlign: 'center' }}>
        <h1>Loading Photo Gallery...</h1>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem', textAlign: 'center' }}>
        <h1>Photo Gallery</h1>
        <p style={{ color: '#dc3545' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      <h1>Family Photo Gallery</h1>
      <p style={{ marginBottom: '2rem' }}>Browse and organize your family photos and historical images</p>

      {/* Controls Bar */}
      <div style={{
        background: 'white',
        padding: '1rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        display: 'flex',
        gap: '1rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <input
          type="text"
          placeholder="Search by name, person, or tag..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: '1',
            minWidth: '200px',
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />

        {/* Filter Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setFilter('all')}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: filter === 'all' ? '#2e7d32' : 'white',
              color: filter === 'all' ? 'white' : '#333',
              cursor: 'pointer'
            }}
          >
            All ({images.length})
          </button>
          <button
            onClick={() => setFilter('untagged')}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: filter === 'untagged' ? '#2e7d32' : 'white',
              color: filter === 'untagged' ? 'white' : '#333',
              cursor: 'pointer'
            }}
          >
            Untagged
          </button>
        </div>

        {/* Selection Controls */}
        {filteredImages.length > 0 && (
          <button
            onClick={selectAll}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            {selectedImages.length === filteredImages.length ? 'Deselect All' : 'Select All'}
          </button>
        )}

        {/* Album Creation */}
        {selectedImages.length > 0 && (
          <button
            onClick={createAlbum}
            style={{
              padding: '0.5rem 1rem',
              background: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Create Album ({selectedImages.length})
          </button>
        )}
      </div>

      {/* Image Grid */}
      {filteredImages.length === 0 ? (
        <div style={{
          background: 'white',
          padding: '3rem',
          borderRadius: '8px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p>No images found matching your criteria.</p>
          <button
            onClick={() => navigate('/documents')}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#2e7d32',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Upload Photos
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredImages.map(image => (
            <div
              key={image._id}
              style={{
                background: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                position: 'relative'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {/* Selection Checkbox */}
              <input
                type="checkbox"
                checked={selectedImages.includes(image._id)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleImageSelection(image._id);
                }}
                style={{
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  zIndex: 1
                }}
              />

              {/* Image */}
              <div
                onClick={() => handleImageClick(image)}
                style={{
                  width: '100%',
                  height: '200px',
                  overflow: 'hidden'
                }}
              >
                <img
                  src={`/api/documents/${image._id}/view?token=${localStorage.getItem('token')}`}
                  alt={image.originalName}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23ddd"/%3E%3Ctext x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Preview%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>

              {/* Image Info */}
              <div style={{ padding: '1rem' }}>
                <h4 style={{ margin: '0 0 0.5rem', fontSize: '14px', color: '#333' }}>
                  {image.originalName}
                </h4>
                
                {/* People Tags */}
                {editingTags === image._id ? (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        placeholder="Person name"
                        value={newPersonName}
                        onChange={(e) => setNewPersonName(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            savePersonTag(image._id);
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: '0.25rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      />
                      <select
                        value={newPersonRole}
                        onChange={(e) => setNewPersonRole(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          padding: '0.25rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        <option value="primary_subject">Primary Subject</option>
                        <option value="spouse">Spouse</option>
                        <option value="child">Child</option>
                        <option value="parent">Parent</option>
                        <option value="sibling">Sibling</option>
                        <option value="other_relative">Other Relative</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          savePersonTag(image._id);
                        }}
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: '#2e7d32',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Add
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTags(null);
                        }}
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {image.peopleIdentified && image.peopleIdentified.length > 0 && (
                      <div style={{ marginTop: '0.5rem' }}>
                        {image.peopleIdentified.map((person, index) => (
                          <span
                            key={index}
                            style={{
                              display: 'inline-block',
                              padding: '0.25rem 0.5rem',
                              margin: '0.25rem 0.25rem 0 0',
                              background: '#e8f4fd',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}
                          >
                            👤 {person.name} ({person.role.replace(/_/g, ' ')})
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removePerson(image._id, index);
                              }}
                              style={{
                                marginLeft: '0.5rem',
                                background: 'none',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={(e) => startEditingTags(image, e)}
                      style={{
                        marginTop: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        background: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      + Tag People
                    </button>
                  </div>
                )}

                {/* Date */}
                <p style={{ margin: '0.5rem 0 0', fontSize: '12px', color: '#666' }}>
                  Uploaded: {new Date(image.uploadedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Full Image Viewer Modal */}
      {viewingImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem'
          }}
          onClick={closeViewer}
        >
          <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
            <img
              src={`/api/documents/${viewingImage._id}/view?token=${localStorage.getItem('token')}`}
              alt={viewingImage.originalName}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain'
              }}
            />
            <button
              onClick={closeViewer}
              style={{
                position: 'absolute',
                top: '-40px',
                right: 0,
                background: 'white',
                color: 'black',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Close
            </button>
            <div style={{
              position: 'absolute',
              bottom: '-60px',
              left: 0,
              right: 0,
              textAlign: 'center',
              color: 'white'
            }}>
              <h3 style={{ margin: '0' }}>{viewingImage.originalName}</h3>
              {viewingImage.peopleIdentified && viewingImage.peopleIdentified.length > 0 && (
                <p style={{ margin: '0.5rem 0' }}>
                  People: {viewingImage.peopleIdentified.map(p => p.name).join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
